import React, { useEffect } from 'react';
import { message, Form, Input, Upload, Button, Space, Avatar, Flex, Image } from 'antd';
import type { FormInstance } from 'antd';
import { PictureOutlined, LoadingOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import { calFileUploadParams, getCoverBase64, getFileNameAndType, randomStr } from '@/utils';
import { useRequest } from 'umi';
import type { RcFile } from 'antd/es/upload';
import { submitFile } from '@/utils/requests/common';
import type { FormProps } from 'antd';
import { v4 as uuidv4 } from 'uuid';

interface CommentFormProps {
  onSubmit: (values: { content: string; imageUrl?: string }) => void;
  toUserName?: string;
  isFocus?: boolean;
}

const CommentForm: React.FC<CommentFormProps> = ({ onSubmit, toUserName, isFocus }) => {
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = React.useState<string>();
  const [uploadUrl, setUploadUrl] = React.useState<string>('');
  const [loading, setLoading] = React.useState(false);
  let uuid = uuidv4();

  const submitFileRequest = useRequest(
    (fileName, contentType) => {
      return submitFile({ path: 'myfr/camp', fileName, contentType });
    },
    {
      manual: true,
    },
  );

  const uploadButton = (
    <div className="flex_1 col808" style={{ gap: 6, marginTop: '-4px' }}>
      <div style={{ marginTop: 2 }}>{loading ? <LoadingOutlined /> : <PictureOutlined />}</div>
      <div className="f14">图片</div>
    </div>
  );

  const beforeUpload = (file: RcFile) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file!').then();
    }
    const uploadSize = file.size / 1024 <= 500;
    if (!uploadSize) {
      message.error('图片大小不能超过500kb');
    }
    return isJpgOrPng && uploadSize;
  };

  const thumbMask = (
    <div key={Math.random()} style={{ borderRadius: 6 }}>
      <DeleteOutlined style={{ fontSize: 20 }} />
    </div>
  );

  const onFinish: FormProps<{ content: string }>['onFinish'] = (values) => {
    const submitValue = {
      content: values.content,
      imageUrl: uploadUrl,
    };
    onSubmit({ ...submitValue });
    form.resetFields();
    setUploadUrl('');
    setImageUrl('');
  };

  const SubmitButton: React.FC<React.PropsWithChildren<{ form: FormInstance }>> = ({
    form,
    children,
  }) => {
    const [submittable, setSubmittable] = React.useState<boolean>(false);
    const values = Form.useWatch([], form);
    React.useEffect(() => {
      form
        .validateFields({ validateOnly: true })
        .then(() => setSubmittable(true))
        .catch(() => setSubmittable(false));
    }, [form, values]);

    return (
      <Button
        type="primary"
        htmlType="submit"
        style={{ width: 100, height: 36 }}
        disabled={!submittable}
      >
        {children}
      </Button>
    );
  };

  // useEffect(() => {
  //   console.log('uuid', uuid);
  //   const customerNameId = document.getElementById(uuid);
  //   customerNameId?.focus();
  // }, [uuid]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const getId = document.getElementById(uuid);
      getId?.focus();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [uuid]);

  return (
    <Form
      name={randomStr()}
      form={form}
      preserve={false}
      onFinish={onFinish}
      onFinishFailed={(errorInfo: any) => {
        console.log('Failed:', errorInfo);
      }}
      autoComplete="off"
    >
      <Form.Item
        name="content"
        style={{ marginBottom: 16 }}
        rules={[{ required: true, message: '请输入评论内容' }]}
      >
        <Input
          id={uuid}
          showCount
          maxLength={500}
          style={{ height: 40, marginTop: 4 }}
          placeholder={toUserName ? `回复给：${toUserName}` : '说说您的看法'}
        />
      </Form.Item>
      <Flex justify="space-between">
        <Form.Item style={{ marginBottom: 0 }}>
          <Upload
            listType="picture-card"
            fileList={[]}
            showUploadList={false}
            customRequest={(info) => {
              let fileInfo = getFileNameAndType((info.file as RcFile).name);
              let uploadParams = calFileUploadParams(fileInfo.type, 'myfr/camp');
              setLoading(true);
              submitFileRequest
                .run(uploadParams.fileName, (info.file as RcFile).type)
                .then((res) => {
                  axios.request({
                    method: 'PUT',
                    url: res.signedUrl,
                    withCredentials: false,
                    headers:
                      Object.assign(res.actualSignedRequestHeaders, {
                        'Content-Disposition': `attachment; filename=${encodeURIComponent(
                          (info.file as RcFile).name,
                        )}`,
                      }) || {},
                    data: info.file,
                  });
                })
                .then(() => {
                  getCoverBase64(info.file, (imageUrl: any) => {
                    setLoading(false);
                    setImageUrl(imageUrl);
                  });
                  setUploadUrl(
                    `https://shequ-oss.obs.cn-east-2.myhuaweicloud.com:443/myfr/camp/${uploadParams.fileName}`,
                  );
                });
            }}
            beforeUpload={beforeUpload}
          >
            {imageUrl ? null : uploadButton}
          </Upload>
          {imageUrl && (
            <Image
              style={{ width: 50, borderRadius: 6 }}
              preview={{
                mask: [thumbMask],
                visible: false,
                onVisibleChange: () => {
                  setImageUrl('');
                  setUploadUrl('');
                },
              }}
              src={imageUrl}
            />
          )}
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Space>
            <SubmitButton form={form}>发布</SubmitButton>
          </Space>
        </Form.Item>
      </Flex>
    </Form>
  );
};

export default CommentForm;
